import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { normalizarTexto } from '../utils/texto';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Users,
    UserPlus,
    Search,
    Shield,
    ShieldCheck,
    ShieldAlert,
    Eye,
    EyeOff,
    Clock,
    ToggleLeft,
    ToggleRight,
    RefreshCw,
    Check,
    X,
    Building2,
    Key,
    AlertCircle,
    Settings,
    UserCog,
    User,
    Link as LinkIcon,
    Trash2,
    Bell,
    BellRing,
    Wifi,
    WifiOff,
    Send,
    CheckCircle2,
    XCircle,
    Info,
    Layers,
    Bug,
    Mail,
    Sparkles
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { AVAILABLE_MODULES, PERMISOS_ACCIONES, isPrivilegedRole } from '../config/comingSoonRoutes';
import { usePermissions } from '../context/PermissionsContext';
import './SettingsView.css';

const ALL_MODULE_PATHS = AVAILABLE_MODULES.map(mod => mod.path);

const ROLES = [
    { value: 'admin', label: 'Administrador', color: '#ef4444', bgColor: '#fef2f2', icon: ShieldAlert },
    { value: 'ti', label: 'Tecnología e Innovación', color: '#0891b2', bgColor: '#ecfeff', icon: ShieldCheck },
    { value: 'gh', label: 'Gestión Humana', color: '#22c55e', bgColor: '#f0fdf4', icon: ShieldCheck },
    { value: 'captacion', label: 'GH Captación', color: '#f59e0b', bgColor: '#fffbeb', icon: ShieldCheck },
    { value: 'recepcion', label: 'Recepción', color: '#8b5cf6', bgColor: '#f5f3ff', icon: Shield },
    { value: 'cxp', label: 'Cuentas por Pagar', color: '#ec4899', bgColor: '#fdf2f8', icon: Shield },
    { value: 'costos', label: 'Costos', color: '#f97316', bgColor: '#fff7ed', icon: Shield },
    { value: 'finanzas', label: 'Finanzas', color: '#0f766e', bgColor: '#ecfdf5', icon: Shield },
    { value: 'mercadeo', label: 'Mercadeo', color: '#06b6d4', bgColor: '#ecfeff', icon: Shield },
    { value: 'contabilidad', label: 'Contabilidad', color: '#14b8a6', bgColor: '#f0fdfa', icon: Shield },
    { value: 'procura', label: 'Procura', color: '#64748b', bgColor: '#f8fafc', icon: Shield },
    { value: 'seguridad', label: 'Seguridad y PCP', color: '#ca8a04', bgColor: '#fefce8', icon: Shield },
    { value: 'generales', label: 'Servicios Generales', color: '#65a30d', bgColor: '#f7fee7', icon: Shield },
    { value: 'user', label: 'Usuario', color: '#6366f1', bgColor: '#eef2ff', icon: User }
];

function SettingsView() {
    const { user } = useAuth();
    const { socket, connected: socketConnected } = useSocket();
    const navigate = useNavigate();

    // Main states
    const [usuarios, setUsuarios] = useState([]);
    const [empleadosSinUsuario, setEmpleadosSinUsuario] = useState([]);
    const [allEmpleados, setAllEmpleados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingEmpleados, setLoadingEmpleados] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchEmpleados, setSearchEmpleados] = useState('');

    // Navigation: sidebar section + sub-tab for usuarios
    const [activeSection, setActiveSection] = useState('usuarios');
    const [usuariosSubTab, setUsuariosSubTab] = useState('gestionar');

    // Modals & actions
    const [showGenerateConfirm, setShowGenerateConfirm] = useState(null);
    const [selectedRole, setSelectedRole] = useState('user');
    const [showPasswordModal, setShowPasswordModal] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(null);
    const [assignSearch, setAssignSearch] = useState('');
    const [assignLoading, setAssignLoading] = useState(false);
    const [feedback, setFeedback] = useState({ show: false, type: '', message: '' });

    // Modules (coming soon) — saved + draft (draft persists changes until "Guardar")
    // comingSoon = Oculto (fuera del sidebar). muyPronto = visible pero tapado.
    const [comingSoonRoutes, setComingSoonRoutes] = useState([]);
    const [draftComingSoonRoutes, setDraftComingSoonRoutes] = useState([]);
    const [muyProntoRoutes, setMuyProntoRoutes] = useState([]);
    const [draftMuyProntoRoutes, setDraftMuyProntoRoutes] = useState([]);
    const [modulesLoading, setModulesLoading] = useState(false);

    // Email notification configs
    const [emailConfigs, setEmailConfigs] = useState([]);
    const [emailConfigsLoading, setEmailConfigsLoading] = useState(false);

    // Role permissions — saved + draft
    const [rolePermissions, setRolePermissions] = useState({});
    const [draftRolePermissions, setDraftRolePermissions] = useState({});
    const [rolePermissionsLoading, setRolePermissionsLoading] = useState(false);
    const [permRole, setPermRole] = useState(
        () => (ROLES.find(r => !isPrivilegedRole(r.value)) || {}).value
    );

    // Rutas que un admin ya decidió. null = la clave aún no se sembró (migración 029)
    // y la regla de "módulo nuevo" queda desactivada.
    const [knownModuleRoutes, setKnownModuleRoutes] = useState(null);
    const [modulesLoaded, setModulesLoaded] = useState(false);
    const [savingChanges, setSavingChanges] = useState(false);
    const { refetchPermissions } = usePermissions();

    // Generate all
    const [generatingAll, setGeneratingAll] = useState(false);

    // Verify admin
    useEffect(() => {
        if (user && user.role !== 'admin' && user.role !== 'ti') {
            navigate('/empleados');
        }
    }, [user, navigate]);

    // Load data
    useEffect(() => {
        fetchUsuarios();
        fetchAllEmpleados();
    }, []);

    useEffect(() => {
        if ((activeSection === 'modulos' || activeSection === 'permisos') && user?.role !== 'admin') {
            setActiveSection('usuarios');
            return;
        }
        if (activeSection === 'usuarios' && usuariosSubTab === 'generar') {
            fetchEmpleadosSinUsuario();
        }
        if (activeSection === 'modulos') {
            fetchComingSoonRoutes();
        }
        if (activeSection === 'correos') {
            fetchEmailConfigs();
        }
        if (activeSection === 'permisos') {
            fetchRolePermissions();
        }
    }, [activeSection, usuariosSubTab, user?.role]);

    const fetchUsuarios = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/api/usuarios');
            setUsuarios(response.data);
            setError(null);
        } catch (err) {
            console.error('Error al cargar usuarios:', err);
            setError('Error al cargar la lista de usuarios');
        } finally {
            setLoading(false);
        }
    };

    const fetchEmpleadosSinUsuario = async () => {
        setLoadingEmpleados(true);
        try {
            const response = await axiosInstance.get('/api/usuarios/empleados-sin-usuario');
            setEmpleadosSinUsuario(response.data);
        } catch (err) {
            console.error('Error al cargar empleados:', err);
        } finally {
            setLoadingEmpleados(false);
        }
    };

    const fetchAllEmpleados = async () => {
        try {
            const response = await axiosInstance.get('/api/empleados');
            setAllEmpleados(response.data);
        } catch (err) {
            console.error('Error al cargar todos los empleados:', err);
        }
    };

    // --- Modules (Coming Soon) ---
    const fetchComingSoonRoutes = async () => {
        setModulesLoading(true);
        try {
            const [csRes, mpRes, knownRes] = await Promise.all([
                axiosInstance.get('/api/settings/coming-soon'),
                axiosInstance.get('/api/settings/muy-pronto'),
                axiosInstance.get('/api/settings/known-modules'),
            ]);
            const savedCs = (csRes.data.routes || []);
            const mpRoutes = (mpRes.data.routes || []);
            const known = knownRes.data.routes ?? null;
            // Un módulo que nadie ha decidido llegó en un deploy: lo sembramos como
            // oculto en la lista guardada y en el draft. Así el deploy no lo destapa,
            // no cuenta como cambio pendiente, y al guardar queda oculto explícito.
            const nuevosSinEstado = known === null
                ? []
                : ALL_MODULE_PATHS.filter(
                    path => !known.includes(path) && !savedCs.includes(path) && !mpRoutes.includes(path)
                );
            const csRoutes = [...savedCs, ...nuevosSinEstado];
            setComingSoonRoutes(csRoutes);
            setDraftComingSoonRoutes(csRoutes);
            setMuyProntoRoutes(mpRoutes);
            setDraftMuyProntoRoutes(mpRoutes);
            setKnownModuleRoutes(known);
            setModulesLoaded(true);
        } catch (err) {
            console.error('Error al cargar módulos:', err);
        } finally {
            setModulesLoading(false);
        }
    };

    const isNewModule = useCallback(
        (path) => knownModuleRoutes !== null && !knownModuleRoutes.includes(path),
        [knownModuleRoutes]
    );

    const newModulePaths = useMemo(
        () => ALL_MODULE_PATHS.filter(isNewModule),
        [isNewModule]
    );

    // Estado de un módulo: 'visible' | 'pronto' | 'oculto' (mutuamente excluyentes).
    const setModuleState = (path, state) => {
        setDraftComingSoonRoutes(prev =>
            state === 'oculto'
                ? (prev.includes(path) ? prev : [...prev, path])
                : prev.filter(r => r !== path)
        );
        setDraftMuyProntoRoutes(prev =>
            state === 'pronto'
                ? (prev.includes(path) ? prev : [...prev, path])
                : prev.filter(r => r !== path)
        );
    };

    const getModuleState = (path) => {
        if (draftMuyProntoRoutes.includes(path)) return 'pronto';
        if (draftComingSoonRoutes.includes(path)) return 'oculto';
        return 'visible';
    };

    const getSavedModuleState = (path) => {
        if (muyProntoRoutes.includes(path)) return 'pronto';
        if (comingSoonRoutes.includes(path)) return 'oculto';
        return 'visible';
    };

    const enabledEmailConfigsCount = useMemo(
        () => emailConfigs.filter(config => config.activo).length,
        [emailConfigs]
    );

    const fetchEmailConfigs = async () => {
        setEmailConfigsLoading(true);
        try {
            const response = await axiosInstance.get('/api/notificacion-email-config');
            setEmailConfigs(response.data);
        } catch (err) {
            console.error('Error al cargar configs de email:', err);
        } finally {
            setEmailConfigsLoading(false);
        }
    };

    const updateEmailConfig = async (tipo, patch, fallbackMessage = 'Error al actualizar') => {
        const prev = [...emailConfigs];
        const currentConfig = emailConfigs.find(c => c.tipo === tipo);
        const nextConfig = {
            ...(currentConfig || {
                tipo,
                label: tipo,
                desc: '',
                grupo: 'Otros',
                activo: false,
                include_notification_recipients: true,
                recipient_roles: [],
                destinos_email: ''
            }),
            ...patch
        };

        setEmailConfigs(emailConfigs.map(c => c.tipo === tipo ? nextConfig : c));

        try {
            const res = await axiosInstance.put(`/api/notificacion-email-config/${tipo}`, {
                activo: nextConfig.activo,
                include_notification_recipients: !!nextConfig.include_notification_recipients,
                recipient_roles: nextConfig.recipient_roles || [],
            });

            setEmailConfigs(current => {
                const others = current.filter(c => c.tipo !== tipo);
                const updated = [...others, res.data];
                return updated.sort((a, b) => {
                    const groupCompare = (a.grupo || '').localeCompare(b.grupo || '');
                    if (groupCompare !== 0) return groupCompare;
                    return (a.label || a.tipo).localeCompare(b.label || b.tipo);
                });
            });
        } catch (err) {
            setEmailConfigs(prev);
            showFeedback('error', fallbackMessage);
        }
    };

    const handleToggleEmailType = async (tipo) => {
        const existing = emailConfigs.find(c => c.tipo === tipo);
        await updateEmailConfig(
            tipo,
            { activo: !existing?.activo },
            'Error al actualizar'
        );
    };

    const handleToggleNotificationRecipients = async (tipo) => {
        const existing = emailConfigs.find(c => c.tipo === tipo);
        await updateEmailConfig(
            tipo,
            { include_notification_recipients: !existing?.include_notification_recipients },
            'Error al actualizar destinatarios'
        );
    };

    const handleToggleEmailRole = async (tipo, role) => {
        const existing = emailConfigs.find(c => c.tipo === tipo);
        const roles = new Set(existing?.recipient_roles || []);
        if (roles.has(role)) {
            roles.delete(role);
        } else {
            roles.add(role);
        }

        await updateEmailConfig(
            tipo,
            { recipient_roles: Array.from(roles).sort() },
            'Error al actualizar roles destinatarios'
        );
    };

    const isEmailEnabledForType = (tipo) => {
        const config = emailConfigs.find(c => c.tipo === tipo);
        return config?.activo || false;
    };

    const getRoleInfo = (role) => {
        return ROLES.find(r => r.value === role) || ROLES[ROLES.length - 1];
    };

    const modulesByGroup = useMemo(() => {
        const groups = {};
        AVAILABLE_MODULES.forEach(mod => {
            if (!groups[mod.group]) groups[mod.group] = [];
            groups[mod.group].push(mod);
        });
        return groups;
    }, []);

    // --- Role Permissions ---
    const fetchRolePermissions = async () => {
        setRolePermissionsLoading(true);
        try {
            const [permRes, knownRes] = await Promise.all([
                axiosInstance.get('/api/settings/role-permissions'),
                axiosInstance.get('/api/settings/known-modules'),
            ]);
            const perms = permRes.data.permissions || {};
            setRolePermissions(perms);
            setDraftRolePermissions(perms);
            setKnownModuleRoutes(knownRes.data.routes ?? null);
        } catch (err) {
            console.error('Error al cargar permisos por rol:', err);
        } finally {
            setRolePermissionsLoading(false);
        }
    };

    const handleToggleRolePermission = (role, path) => {
        setDraftRolePermissions(prev => {
            const rolePaths = [...(prev[role] || [])];
            const hasAccess = rolePaths.includes(path);
            const newRolePaths = hasAccess
                ? rolePaths.filter(p => p !== path)
                : [...rolePaths, path];
            return { ...prev, [role]: newRolePaths };
        });
    };

    // --- Save / Cancel (drafts -> server) ---
    const modulesChangesCount = useMemo(() => {
        // Contamos módulos cuyo estado (visible/pronto/oculto) cambió respecto a lo guardado.
        const paths = new Set([
            ...comingSoonRoutes, ...draftComingSoonRoutes,
            ...muyProntoRoutes, ...draftMuyProntoRoutes,
        ]);
        let count = 0;
        paths.forEach(path => {
            const saved = muyProntoRoutes.includes(path) ? 'pronto' : comingSoonRoutes.includes(path) ? 'oculto' : 'visible';
            const draft = draftMuyProntoRoutes.includes(path) ? 'pronto' : draftComingSoonRoutes.includes(path) ? 'oculto' : 'visible';
            if (saved !== draft) count++;
        });
        return count;
    }, [comingSoonRoutes, draftComingSoonRoutes, muyProntoRoutes, draftMuyProntoRoutes]);

    // Cambios sin guardar por rol, para marcar en cuáles quedó trabajo pendiente.
    const roleDirtyCounts = useMemo(() => {
        const counts = {};
        const roles = new Set([
            ...Object.keys(rolePermissions || {}),
            ...Object.keys(draftRolePermissions || {})
        ]);
        roles.forEach(role => {
            const a = new Set(rolePermissions[role] || []);
            const b = new Set(draftRolePermissions[role] || []);
            let count = 0;
            a.forEach(x => { if (!b.has(x)) count++; });
            b.forEach(x => { if (!a.has(x)) count++; });
            if (count > 0) counts[role] = count;
        });
        return counts;
    }, [rolePermissions, draftRolePermissions]);

    const permissionsChangesCount = useMemo(
        () => Object.values(roleDirtyCounts).reduce((sum, count) => sum + count, 0),
        [roleDirtyCounts]
    );

    const totalPendingChanges = modulesChangesCount + permissionsChangesCount;
    const isDirty = totalPendingChanges > 0;

    const handleSaveChanges = async () => {
        if (!isDirty || savingChanges) return;
        setSavingChanges(true);
        try {
            const tasks = [];
            // El guard de modulesLoaded evita mandar drafts vacíos (y destapar todo)
            // si el admin nunca abrió la pestaña de módulos en esta sesión.
            if (modulesLoaded && (modulesChangesCount > 0 || newModulePaths.length > 0)) {
                tasks.push(
                    axiosInstance.put('/api/settings/coming-soon', { routes: draftComingSoonRoutes })
                );
                tasks.push(
                    axiosInstance.put('/api/settings/muy-pronto', { routes: draftMuyProntoRoutes })
                );
                // Todo lo que está en pantalla queda registrado como decidido: solo lo
                // que llegue en el próximo deploy nacerá oculto.
                tasks.push(
                    axiosInstance.put('/api/settings/known-modules', { routes: ALL_MODULE_PATHS })
                );
            }
            if (permissionsChangesCount > 0) {
                tasks.push(
                    axiosInstance.put('/api/settings/role-permissions', { permissions: draftRolePermissions })
                );
            }
            await Promise.all(tasks);
            setComingSoonRoutes(draftComingSoonRoutes);
            setMuyProntoRoutes(draftMuyProntoRoutes);
            setRolePermissions(draftRolePermissions);
            if (modulesLoaded) {
                setKnownModuleRoutes(ALL_MODULE_PATHS);
            }
            refetchPermissions();
            showFeedback('success', 'Cambios guardados');
        } catch (err) {
            showFeedback('error', 'Error al guardar los cambios');
        } finally {
            setSavingChanges(false);
        }
    };

    const handleCancelChanges = () => {
        setDraftComingSoonRoutes(comingSoonRoutes);
        setDraftMuyProntoRoutes(muyProntoRoutes);
        setDraftRolePermissions(rolePermissions);
        showFeedback('success', 'Cambios descartados');
    };

    const permissionModulesByGroup = useMemo(() => {
        const groups = {};
        [...AVAILABLE_MODULES, ...PERMISOS_ACCIONES].forEach(mod => {
            if (!groups[mod.group]) groups[mod.group] = [];
            groups[mod.group].push(mod);
        });
        return groups;
    }, []);

    const nonAdminRoles = useMemo(() => ROLES.filter(r => !isPrivilegedRole(r.value)), []);

    const activeRole = useMemo(
        () => nonAdminRoles.find(r => r.value === permRole) || nonAdminRoles[0],
        [nonAdminRoles, permRole]
    );

    const showFeedback = (type, message) => {
        setFeedback({ show: true, type, message });
        setTimeout(() => setFeedback({ show: false, type: '', message: '' }), 4000);
    };

    // Filter & sort users
    const filteredUsuarios = useMemo(() => {
        let result = usuarios.filter(u => u.empleado_activo !== false);
        if (searchTerm.trim()) {
            const term = normalizarTexto(searchTerm);
            result = result.filter(u =>
                normalizarTexto(u.username).includes(term) ||
                normalizarTexto(u.nombre_empleado).includes(term) ||
                normalizarTexto(u.role).includes(term)
            );
        }
        const rolePriority = { 'admin': 1, 'gh': 2, 'user': 3 };
        return result.sort((a, b) => {
            const priorityA = rolePriority[a.role] || 99;
            const priorityB = rolePriority[b.role] || 99;
            if (priorityA !== priorityB) return priorityA - priorityB;
            return a.username.localeCompare(b.username);
        });
    }, [usuarios, searchTerm]);

    const filteredEmpleados = useMemo(() => {
        if (!searchEmpleados.trim()) return empleadosSinUsuario;
        const term = normalizarTexto(searchEmpleados);
        return empleadosSinUsuario.filter(e =>
            normalizarTexto(e.nombre_completo).includes(term) ||
            normalizarTexto(e.cedula).includes(term) ||
            normalizarTexto(e.cargo).includes(term) ||
            normalizarTexto(e.departamento).includes(term)
        );
    }, [empleadosSinUsuario, searchEmpleados]);

    const filteredAssignEmpleados = useMemo(() => {
        const empleadosConUsuario = new Set(usuarios.map(u => u.empleado_id).filter(Boolean));
        let filtered = allEmpleados.filter(e => !empleadosConUsuario.has(e.id));
        if (assignSearch.trim()) {
            const term = normalizarTexto(assignSearch);
            filtered = filtered.filter(e =>
                normalizarTexto(e.nombre).includes(term) ||
                normalizarTexto(e.apellido).includes(term) ||
                normalizarTexto(e.cedula).includes(term)
            );
        }
        return filtered.slice(0, 10);
    }, [allEmpleados, usuarios, assignSearch]);

    // Handlers
    const handleToggleActive = async (usuarioId) => {
        try {
            const response = await axiosInstance.patch(`/api/usuarios/${usuarioId}/toggle-active`);
            setUsuarios(prev => prev.map(u =>
                u.id === usuarioId ? { ...u, is_active: response.data.usuario.is_active } : u
            ));
            showFeedback('success', response.data.message);
        } catch (err) {
            showFeedback('error', err.response?.data?.error || 'Error al cambiar estado');
        }
    };

    const handleDeleteUser = async (usuario) => {
        if (!window.confirm(`¿Desactivar al usuario "${usuario.username}"?\n\nNo podrá iniciar sesión pero sus datos se conservarán.`)) {
            return;
        }
        try {
            const response = await axiosInstance.delete(`/api/usuarios/${usuario.id}`);
            showFeedback('success', response.data.message);
            setUsuarios(prev => prev.map(u => u.id === usuario.id ? { ...u, is_active: false } : u));
        } catch (err) {
            showFeedback('error', err.response?.data?.error || 'Error al desactivar usuario');
        }
    };

    const handleChangePassword = async () => {
        if (!newPassword.trim()) {
            showFeedback('error', 'La contraseña no puede estar vacía');
            return;
        }
        if (newPassword.length < 4) {
            showFeedback('error', 'La contraseña debe tener al menos 4 caracteres');
            return;
        }
        setPasswordLoading(true);
        try {
            const response = await axiosInstance.patch(`/api/usuarios/${showPasswordModal}/change-password`, {
                password: newPassword
            });
            showFeedback('success', response.data.message);
            setShowPasswordModal(null);
            setNewPassword('');
        } catch (err) {
            showFeedback('error', err.response?.data?.error || 'Error al cambiar contraseña');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleAssignEmployee = async (empleadoId) => {
        setAssignLoading(true);
        try {
            const response = await axiosInstance.patch(`/api/usuarios/${showAssignModal}/assign-employee`, {
                empleado_id: empleadoId
            });
            setUsuarios(prev => prev.map(u =>
                u.id === showAssignModal ? response.data.usuario : u
            ));
            showFeedback('success', response.data.message);
            setShowAssignModal(null);
            setAssignSearch('');
            fetchEmpleadosSinUsuario();
        } catch (err) {
            showFeedback('error', err.response?.data?.error || 'Error al asignar empleado');
        } finally {
            setAssignLoading(false);
        }
    };

    const handleGenerateAll = async () => {
        if (!window.confirm(`¿Generar usuarios para los ${empleadosSinUsuario.length} empleados sin cuenta?\n\nSe asignará rol "Usuario" y la contraseña será la cédula de cada empleado.`)) {
            return;
        }
        setGeneratingAll(true);
        try {
            const response = await axiosInstance.post('/api/usuarios/generate-all', {}, { timeout: 120000 });
            const { created, skipped, skipped_names } = response.data;
            let msg = `${created} usuarios creados.`;
            if (skipped > 0) {
                msg += ` ${skipped} omitidos (sin cédula): ${skipped_names.join(', ')}`;
            }
            showFeedback('success', msg);
            fetchUsuarios();
            fetchEmpleadosSinUsuario();
        } catch (err) {
            showFeedback('error', err.response?.data?.error || 'Error al generar usuarios');
        } finally {
            setGeneratingAll(false);
        }
    };

    const handleGenerateUser = async (empleadoId) => {
        try {
            const response = await axiosInstance.post(`/api/usuarios/generate/${empleadoId}`, {
                role: selectedRole
            });
            showFeedback('success', `Usuario "${response.data.usuario.username}" creado exitosamente`);
            setShowGenerateConfirm(null);
            fetchUsuarios();
            fetchEmpleadosSinUsuario();
        } catch (err) {
            showFeedback('error', err.response?.data?.error || 'Error al generar usuario');
        }
    };

    // ===== Notification Diagnostics =====
    const logIdRef = useRef(0);
    const [notifPermission, setNotifPermission] = useState(
        'Notification' in window ? Notification.permission : 'unsupported'
    );
    const [notifTestLog, setNotifTestLog] = useState([]);
    const [serverTestLoading, setServerTestLoading] = useState(false);

    // Anuncios debug
    const [anunciosRefreshing, setAnunciosRefreshing] = useState(false);
    const [anunciosResult, setAnunciosResult] = useState(null);

    const addLog = useCallback((type, message) => {
        logIdRef.current += 1;
        const id = logIdRef.current;
        setNotifTestLog(prev => [
            { id, type, message, time: new Date().toLocaleTimeString() },
            ...prev
        ].slice(0, 15));
    }, []);

    const handleRequestPermission = async () => {
        if (!('Notification' in window)) {
            addLog('error', 'Este navegador no soporta notificaciones');
            return;
        }
        try {
            const result = await Notification.requestPermission();
            setNotifPermission(result);
            addLog(result === 'granted' ? 'success' : 'warning', `Permiso: ${result}`);
        } catch (err) {
            addLog('error', `Error al pedir permiso: ${err.message}`);
        }
    };

    const handleLocalNotification = () => {
        if (Notification.permission !== 'granted') {
            addLog('error', 'Permiso no concedido. Haz click en "Pedir permiso" primero.');
            return;
        }
        try {
            const notif = new Notification('Prueba Local - Centro SURA', {
                body: 'Esta notificacion se genero directamente desde el navegador (sin servidor).',
                icon: '/logo192.png',
                tag: `test-local-${Date.now()}`
            });
            notif.onclick = () => { window.focus(); notif.close(); };
            addLog('success', 'Notificacion local enviada! Revisa tu barra de notificaciones.');
        } catch (err) {
            addLog('error', `Error: ${err.message}`);
        }
    };

    const handleBackgroundNotification = () => {
        if (Notification.permission !== 'granted') {
            addLog('error', 'Permiso no concedido.');
            return;
        }
        addLog('info', 'Tienes 5 segundos para cambiar de tab...');
        setTimeout(() => {
            if (document.visibilityState === 'visible') {
                addLog('warning', 'El tab sigue enfocado. La notificacion se envio pero el sistema la bloquea porque estas viendo la pagina. Cambia de tab y reintenta.');
            }
            const notif = new Notification('Prueba Background - Centro SURA', {
                body: 'Si ves esto, las notificaciones en background funcionan correctamente!',
                icon: '/logo192.png',
                tag: `test-bg-${Date.now()}`
            });
            notif.onclick = () => { window.focus(); notif.close(); };
            if (document.visibilityState !== 'visible') {
                addLog('success', 'Notificacion background enviada!');
            }
        }, 5000);
    };

    const handleServerNotification = async () => {
        if (!user?.id) {
            addLog('error', 'No hay usuario logueado');
            return;
        }
        setServerTestLoading(true);
        try {
            const response = await axiosInstance.post('/api/notificaciones/test', {
                user_id: user.id,
                titulo: 'Prueba desde Ajustes',
                mensaje: 'Notificacion de prueba generada desde el panel de diagnostico.',
                tipo: 'test'
            });
            const data = response.data;
            const emitido = data.notificaciones?.[0]?.socket_emitido;
            addLog('success', `Creada en BD (id: ${data.notificaciones?.[0]?.id}). Socket emitido: ${emitido ? 'SI' : 'NO'}`);
            if (!emitido) {
                addLog('warning', 'El socket no emitio. Usuarios conectados al socket: ' +
                    (data.usuarios_conectados?.length > 0 ? data.usuarios_conectados.join(', ') : 'ninguno'));
            }
        } catch (err) {
            addLog('error', `Error del servidor: ${err.response?.data?.error || err.message}`);
        } finally {
            setServerTestLoading(false);
        }
    };

    useEffect(() => {
        if (!socket) return;
        const handler = (notification) => {
            if (notification.tipo === 'test') {
                addLog('success', `Socket recibio notificacion id:${notification.id} - "${notification.titulo}"`);
            }
        };
        socket.on('new_notification', handler);
        return () => socket.off('new_notification', handler);
    }, [socket, addLog]);

    // Anuncios debug handler
    const handleRefreshAnuncios = async () => {
        setAnunciosRefreshing(true);
        setAnunciosResult(null);
        try {
            const refreshRes = await axiosInstance.post('/api/anuncios/refresh');
            // Esperar a que procese
            await new Promise(r => setTimeout(r, 3000));
            const listRes = await axiosInstance.get('/api/anuncios');
            const count = listRes.data.success ? (listRes.data.emails?.length || 0) : 0;
            setAnunciosResult({ type: 'success', message: `Sincronización completada. ${count} anuncio${count !== 1 ? 's' : ''} en total.` });
        } catch (err) {
            setAnunciosResult({ type: 'error', message: err.response?.data?.error || err.message });
        } finally {
            setAnunciosRefreshing(false);
        }
    };

    if (user?.role !== 'admin' && user?.role !== 'ti') {
        return null;
    }

    // ===== RENDER =====
    return (
        <div className="settings-view">
            {/* Feedback Toast */}
            <AnimatePresence>
                {feedback.show && (
                    <motion.div
                        className={`feedback-toast ${feedback.type}`}
                        initial={{ opacity: 0, y: -20, x: '-50%' }}
                        animate={{ opacity: 1, y: 0, x: '-50%' }}
                        exit={{ opacity: 0, y: -20, x: '-50%' }}
                    >
                        {feedback.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
                        <span>{feedback.message}</span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Password Modal */}
            <AnimatePresence>
                {showPasswordModal && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowPasswordModal(null)}
                    >
                        <motion.div
                            className="modal-content password-modal"
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <Key size={18} />
                                <h3>Cambiar Contraseña</h3>
                            </div>
                            <div className="modal-body">
                                <label>Nueva Contraseña</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    placeholder="Ingresa la nueva contraseña..."
                                    autoFocus
                                />
                            </div>
                            <div className="modal-footer">
                                <button className="btn-secondary" onClick={() => { setShowPasswordModal(null); setNewPassword(''); }}>
                                    Cancelar
                                </button>
                                <button className="btn-primary" onClick={handleChangePassword} disabled={passwordLoading || !newPassword.trim()}>
                                    {passwordLoading ? <RefreshCw className="spin" size={14} /> : <Check size={14} />}
                                    Guardar
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Assign Employee Modal */}
            <AnimatePresence>
                {showAssignModal && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowAssignModal(null)}
                    >
                        <motion.div
                            className="modal-content assign-modal"
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <LinkIcon size={18} />
                                <h3>Asignar Empleado</h3>
                            </div>
                            <div className="modal-body">
                                <div className="assign-search">
                                    <Search size={15} />
                                    <input
                                        type="text"
                                        value={assignSearch}
                                        onChange={e => setAssignSearch(e.target.value)}
                                        placeholder="Buscar empleado..."
                                        autoFocus
                                    />
                                </div>
                                <div className="assign-list">
                                    {filteredAssignEmpleados.length > 0 ? (
                                        filteredAssignEmpleados.map(emp => (
                                            <button key={emp.id} className="assign-item" onClick={() => handleAssignEmployee(emp.id)} disabled={assignLoading}>
                                                <div className="assign-item-info">
                                                    <span className="name">{emp.nombre} {emp.apellido}</span>
                                                    <span className="cedula">C.I: {emp.cedula}</span>
                                                </div>
                                                <Check size={15} />
                                            </button>
                                        ))
                                    ) : (
                                        <div className="assign-empty">
                                            {assignSearch ? 'No se encontraron empleados' : 'Escribe para buscar...'}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn-secondary" onClick={() => { setShowAssignModal(null); setAssignSearch(''); }}>
                                    Cancelar
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Mobile Header */}
            <div className="settings-mobile-header">
                <div className="mobile-icon">
                    <Settings size={20} />
                </div>
                <div>
                    <h1>Ajustes</h1>
                    <p>Panel de administración</p>
                </div>
            </div>

            {/* Layout: Sidebar + Main */}
            <div className="settings-layout">
                {/* Sidebar */}
                <aside className="settings-sidebar">
                    <div className="sidebar-header">
                        <h1>Ajustes</h1>
                        <p>Panel de administración</p>
                    </div>
                    <nav className="sidebar-nav">
                        <p className="sidebar-section-label">General</p>
                        <button
                            className={`sidebar-item ${activeSection === 'usuarios' ? 'active' : ''}`}
                            onClick={() => setActiveSection('usuarios')}
                        >
                            <Users size={17} />
                            <span>Usuarios</span>
                            <span className="sidebar-badge">{usuarios.length}</span>
                        </button>
                        {user?.role === 'admin' && (
                            <>
                                <button
                                    className={`sidebar-item ${activeSection === 'modulos' ? 'active' : ''}`}
                                    onClick={() => setActiveSection('modulos')}
                                >
                                    <Layers size={17} />
                                    <span>Ocultar Módulos</span>
                                    {(draftComingSoonRoutes.length + draftMuyProntoRoutes.length) > 0 && (
                                        <span className="sidebar-badge">{draftComingSoonRoutes.length + draftMuyProntoRoutes.length}</span>
                                    )}
                                    {modulesChangesCount > 0 && (
                                        <span className="sidebar-dirty-dot" title={`${modulesChangesCount} cambio${modulesChangesCount !== 1 ? 's' : ''} sin guardar`} />
                                    )}
                                </button>

                                <button
                                    className={`sidebar-item ${activeSection === 'permisos' ? 'active' : ''}`}
                                    onClick={() => setActiveSection('permisos')}
                                >
                                    <Shield size={17} />
                                    <span>Permisos por Rol</span>
                                    {permissionsChangesCount > 0 && (
                                        <span className="sidebar-dirty-dot" title={`${permissionsChangesCount} cambio${permissionsChangesCount !== 1 ? 's' : ''} sin guardar`} />
                                    )}
                                </button>
                            </>
                        )}

                        <button
                            className={`sidebar-item ${activeSection === 'correos' ? 'active' : ''}`}
                            onClick={() => setActiveSection('correos')}
                        >
                            <Mail size={17} />
                            <span>Correos Notif.</span>
                            {enabledEmailConfigsCount > 0 && (
                                <span className="sidebar-badge">{enabledEmailConfigsCount}</span>
                            )}
                        </button>

                        <p className="sidebar-section-label">Avanzado</p>
                        <button
                            className={`sidebar-item ${activeSection === 'debug' ? 'active' : ''}`}
                            onClick={() => setActiveSection('debug')}
                        >
                            <Bug size={17} />
                            <span>Debug</span>
                        </button>
                    </nav>
                </aside>

                {/* Main Content */}
                <main className="settings-main">
                    {/* ============================================
                        SECTION: USUARIOS
                       ============================================ */}
                    {activeSection === 'usuarios' && (
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="settings-section-header">
                                <h2>Usuarios</h2>
                                <p>Gestiona las cuentas de usuario del sistema</p>
                            </div>

                            <div className="settings-card">
                                {/* Sub-tabs: Gestionar | Generar */}
                                <div className="sub-tabs">
                                    <button
                                        className={`sub-tab ${usuariosSubTab === 'gestionar' ? 'active' : ''}`}
                                        onClick={() => setUsuariosSubTab('gestionar')}
                                    >
                                        <UserCog size={15} />
                                        Gestionar
                                        <span className="sub-tab-badge">{usuarios.length}</span>
                                    </button>
                                    <button
                                        className={`sub-tab ${usuariosSubTab === 'generar' ? 'active' : ''}`}
                                        onClick={() => setUsuariosSubTab('generar')}
                                    >
                                        <UserPlus size={15} />
                                        Generar
                                        {empleadosSinUsuario.length > 0 && (
                                            <span className="sub-tab-badge">{empleadosSinUsuario.length}</span>
                                        )}
                                    </button>
                                </div>

                                {/* Sub-tab: Gestionar Usuarios */}
                                {usuariosSubTab === 'gestionar' && (
                                    <div>
                                        <div className="settings-search">
                                            <Search size={16} className="search-icon" />
                                            <input
                                                type="text"
                                                placeholder="Buscar por username, nombre o rol..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                            {searchTerm && (
                                                <button className="clear-search" onClick={() => setSearchTerm('')}>
                                                    <X size={14} />
                                                </button>
                                            )}
                                        </div>

                                        {loading ? (
                                            <div className="loading-state">
                                                <RefreshCw className="spin" size={22} />
                                                <span>Cargando usuarios...</span>
                                            </div>
                                        ) : error ? (
                                            <div className="error-state">
                                                <AlertCircle size={22} />
                                                <span>{error}</span>
                                                <button onClick={fetchUsuarios}>Reintentar</button>
                                            </div>
                                        ) : (
                                            <div className="users-table-wrapper">
                                                <table className="users-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Usuario</th>
                                                            <th>Empleado</th>
                                                            <th>Rol</th>
                                                            <th>Estado</th>
                                                            <th>Último Acceso</th>
                                                            <th>Acciones</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredUsuarios.map((usuario) => {
                                                            const roleInfo = getRoleInfo(usuario.role);
                                                            const RoleIcon = roleInfo.icon;
                                                            return (
                                                                <tr
                                                                    key={usuario.id}
                                                                    className={!usuario.is_active ? 'inactive' : ''}
                                                                >
                                                                    <td>
                                                                        <div className="user-cell">
                                                                            <div className="user-avatar">
                                                                                <UserCog size={14} />
                                                                            </div>
                                                                            <span className="username">{usuario.username}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        {usuario.nombre_empleado ? (
                                                                            <div className="employee-cell">
                                                                                <Building2 size={13} />
                                                                                <span>{usuario.nombre_empleado}</span>
                                                                            </div>
                                                                        ) : (
                                                                            <button
                                                                                className="no-employee-btn"
                                                                                onClick={() => setShowAssignModal(usuario.id)}
                                                                                title="Click para asignar empleado"
                                                                            >
                                                                                <LinkIcon size={11} />
                                                                                Asignar
                                                                            </button>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className="role-badge"
                                                                            style={{
                                                                                backgroundColor: roleInfo.bgColor,
                                                                                color: roleInfo.color,
                                                                                borderColor: roleInfo.color
                                                                            }}
                                                                        >
                                                                            <RoleIcon size={11} />
                                                                            {roleInfo.label}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <button
                                                                            className={`status-toggle ${usuario.is_active ? 'active' : 'inactive'}`}
                                                                            onClick={() => handleToggleActive(usuario.id)}
                                                                            disabled={usuario.id === user?.id}
                                                                            title={usuario.id === user?.id ? 'No puedes desactivarte a ti mismo' : ''}
                                                                        >
                                                                            {usuario.is_active ? (
                                                                                <><Eye size={12} /><span>Activo</span></>
                                                                            ) : (
                                                                                <><EyeOff size={12} /><span>Inactivo</span></>
                                                                            )}
                                                                        </button>
                                                                    </td>
                                                                    <td>
                                                                        <span className="last-login">
                                                                            {usuario.last_login
                                                                                ? new Date(usuario.last_login).toLocaleDateString('es-VE', {
                                                                                    day: '2-digit', month: 'short', year: 'numeric',
                                                                                    hour: '2-digit', minute: '2-digit'
                                                                                })
                                                                                : 'Nunca'}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <div className="action-buttons">
                                                                            <button className="action-btn password" onClick={() => setShowPasswordModal(usuario.id)} title="Cambiar contraseña">
                                                                                <Key size={13} />
                                                                            </button>
                                                                            <button
                                                                                className={`action-btn toggle-active ${usuario.is_active ? 'deactivate' : 'activate'}`}
                                                                                onClick={() => handleToggleActive(usuario.id)}
                                                                                disabled={usuario.id === user?.id}
                                                                                title={usuario.id === user?.id ? 'No puedes desactivarte a ti mismo' : usuario.is_active ? 'Desactivar' : 'Activar'}
                                                                            >
                                                                                {usuario.is_active ? <EyeOff size={13} /> : <Eye size={13} />}
                                                                            </button>
                                                                            {usuario.is_active && usuario.id !== user?.id && (
                                                                            <button
                                                                                className="action-btn delete"
                                                                                onClick={() => handleDeleteUser(usuario)}
                                                                                title="Desactivar"
                                                                            >
                                                                                <Trash2 size={13} />
                                                                            </button>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                                {filteredUsuarios.length === 0 && (
                                                    <div className="empty-state">
                                                        <Users size={40} />
                                                        <h3>No se encontraron usuarios</h3>
                                                        <p>Intenta con otros términos de búsqueda</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Sub-tab: Generar Usuarios */}
                                {usuariosSubTab === 'generar' && (
                                    <div className="generate-section-inner">
                                        <div className="generate-header">
                                            <div className="generate-header-info">
                                                <h4>Empleados sin cuenta</h4>
                                                <p>El username se genera automáticamente. Contraseña = cédula.</p>
                                            </div>
                                            {empleadosSinUsuario.length > 0 && (
                                                <button className="generate-all-btn" onClick={handleGenerateAll} disabled={generatingAll || loadingEmpleados}>
                                                    {generatingAll ? <RefreshCw className="spin" size={14} /> : <Users size={14} />}
                                                    Generar todos ({empleadosSinUsuario.length})
                                                </button>
                                            )}
                                        </div>

                                        <div className="settings-search">
                                            <Search size={16} className="search-icon" />
                                            <input
                                                type="text"
                                                placeholder="Buscar empleado por nombre, cédula, cargo..."
                                                value={searchEmpleados}
                                                onChange={(e) => setSearchEmpleados(e.target.value)}
                                            />
                                            {searchEmpleados && (
                                                <button className="clear-search" onClick={() => setSearchEmpleados('')}>
                                                    <X size={14} />
                                                </button>
                                            )}
                                        </div>

                                        {loadingEmpleados ? (
                                            <div className="loading-state">
                                                <RefreshCw className="spin" size={22} />
                                                <span>Cargando empleados...</span>
                                            </div>
                                        ) : (
                                            <div className="employees-grid">
                                                {filteredEmpleados.map((empleado) => (
                                                    <div key={empleado.id} className="employee-card">
                                                        <div className="employee-info">
                                                            <h4>{empleado.nombre_completo}</h4>
                                                            <div className="employee-details">
                                                                <span className="detail">
                                                                    <Building2 size={11} />
                                                                    {empleado.departamento || 'Sin departamento'}
                                                                </span>
                                                                <span className="detail">
                                                                    {empleado.cargo || 'Sin cargo'}
                                                                </span>
                                                            </div>
                                                            <div className="employee-meta">
                                                                <span className="cedula">C.I: {empleado.cedula || 'N/A'}</span>
                                                                <span className="suggested-user">
                                                                    <span className="label">User:</span>
                                                                    <code>{empleado.suggested_username}</code>
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="employee-actions">
                                                            {showGenerateConfirm === empleado.id ? (
                                                                <div className="confirm-generate">
                                                                    <div className="role-select">
                                                                        <label>Rol:</label>
                                                                        <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
                                                                            {ROLES.map(role => (
                                                                                <option key={role.value} value={role.value}>{role.label}</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                    <div className="confirm-buttons">
                                                                        <button className="confirm-btn" onClick={() => handleGenerateUser(empleado.id)}>
                                                                            <Check size={13} /> Confirmar
                                                                        </button>
                                                                        <button className="cancel-btn" onClick={() => setShowGenerateConfirm(null)}>
                                                                            <X size={13} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    className="generate-btn"
                                                                    onClick={() => { setSelectedRole('user'); setShowGenerateConfirm(empleado.id); }}
                                                                >
                                                                    <UserPlus size={14} />
                                                                    Generar
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                                {filteredEmpleados.length === 0 && !loadingEmpleados && (
                                                    <div className="empty-state">
                                                        <Check size={40} />
                                                        <h3>Todos los empleados tienen cuenta</h3>
                                                        <p>No hay empleados sin usuario asignado</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {/* ============================================
                        SECTION: OCULTAR MÓDULOS
                       ============================================ */}
                    {activeSection === 'modulos' && (
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="settings-section-header">
                                <h2>Ocultar Módulos</h2>
                                <p><b>Visible</b>: en el sidebar y navegable. <b>Muy pronto</b>: aparece en el sidebar pero al entrar se tapa con un aviso. <b>Oculto</b>: fuera del sidebar. Los cambios solo se aplican al guardar.</p>
                            </div>

                            {!modulesLoading && newModulePaths.length > 0 && (
                                <div className="modules-new-notice">
                                    <Sparkles size={15} />
                                    <p>
                                        {newModulePaths.length === 1
                                            ? 'Llegó 1 módulo nuevo en el último despliegue. Está oculto hasta que decidas quién lo ve.'
                                            : `Llegaron ${newModulePaths.length} módulos nuevos en el último despliegue. Están ocultos hasta que decidas quién los ve.`}
                                    </p>
                                </div>
                            )}

                            <div className="settings-card">
                                {modulesLoading ? (
                                    <div className="loading-state">
                                        <RefreshCw size={22} className="spin" />
                                    </div>
                                ) : (
                                    <div className="modules-section">
                                        <div className="modules-groups">
                                            {Object.entries(modulesByGroup).map(([group, modules]) => (
                                                <div key={group} className="modules-group">
                                                    <h3 className="modules-group-title">{group}</h3>
                                                    <div className="modules-list">
                                                        {modules.map(mod => {
                                                            const state = getModuleState(mod.path);
                                                            const savedState = getSavedModuleState(mod.path);
                                                            const isDirtyRow = state !== savedState;
                                                            const isNew = isNewModule(mod.path);
                                                            return (
                                                                <div key={mod.path} className={`module-item ${state === 'oculto' ? 'module-hidden' : ''} ${isDirtyRow ? 'module-dirty' : ''}`}>
                                                                    <div className="module-info">
                                                                        <span className="module-label">
                                                                            {mod.label}
                                                                            {isNew && (
                                                                                <span className="module-new-badge">
                                                                                    <Sparkles size={9} />
                                                                                    Nuevo
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                        <span className="module-path">{mod.path}</span>
                                                                    </div>
                                                                    <div className="module-toggle-wrapper">
                                                                        {isDirtyRow && (
                                                                            <span className="module-pending-dot" title="Cambio sin guardar" />
                                                                        )}
                                                                        <div className="module-state-seg" role="group" aria-label={`Estado de ${mod.label}`}>
                                                                            <button
                                                                                type="button"
                                                                                className={`mss-seg state-visible ${state === 'visible' ? 'is-active' : ''}`}
                                                                                onClick={() => setModuleState(mod.path, 'visible')}
                                                                                title="Visible y navegable"
                                                                            >
                                                                                <Eye size={12} />
                                                                                <span>Visible</span>
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className={`mss-seg state-pronto ${state === 'pronto' ? 'is-active' : ''}`}
                                                                                onClick={() => setModuleState(mod.path, 'pronto')}
                                                                                title="Aparece en el sidebar pero tapado con 'Muy pronto'"
                                                                            >
                                                                                <Clock size={12} />
                                                                                <span>Muy pronto</span>
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className={`mss-seg state-oculto ${state === 'oculto' ? 'is-active' : ''}`}
                                                                                onClick={() => setModuleState(mod.path, 'oculto')}
                                                                                title="Fuera del sidebar"
                                                                            >
                                                                                <EyeOff size={12} />
                                                                                <span>Oculto</span>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {/* ============================================
                        SECTION: PERMISOS POR ROL
                       ============================================ */}
                    {activeSection === 'permisos' && (
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="settings-section-header">
                                <h2>Permisos por Rol</h2>
                                <p>Elige un rol y marca los módulos que puede ver. Admin y TI siempre tienen acceso total. Los cambios solo se aplican al guardar.</p>
                            </div>

                            <div className="settings-card">
                                {rolePermissionsLoading ? (
                                    <div className="loading-state">
                                        <RefreshCw size={22} className="spin" />
                                    </div>
                                ) : (
                                    <div className="rp-layout">
                                        <div className="rp-roles" role="tablist" aria-label="Roles">
                                            {nonAdminRoles.map(role => {
                                                const isActive = role.value === activeRole?.value;
                                                const grantedCount = (draftRolePermissions[role.value] || []).length;
                                                const dirtyCount = roleDirtyCounts[role.value] || 0;
                                                return (
                                                    <button
                                                        key={role.value}
                                                        type="button"
                                                        role="tab"
                                                        aria-selected={isActive}
                                                        className={`rp-role ${isActive ? 'is-active' : ''}`}
                                                        onClick={() => setPermRole(role.value)}
                                                    >
                                                        <span
                                                            className="rp-role-dot"
                                                            style={{ background: role.color }}
                                                        />
                                                        <span className="rp-role-name">{role.label}</span>
                                                        {dirtyCount > 0 && (
                                                            <span
                                                                className="rp-role-pending"
                                                                title={dirtyCount === 1 ? '1 cambio sin guardar' : `${dirtyCount} cambios sin guardar`}
                                                            />
                                                        )}
                                                        <span className="rp-role-count">{grantedCount}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {activeRole && (
                                            <div
                                                className="rp-detail"
                                                role="tabpanel"
                                                aria-label={`Módulos de ${activeRole.label}`}
                                            >
                                                <div className="rp-detail-header">
                                                    <span
                                                        className="rp-detail-badge"
                                                        style={{
                                                            color: activeRole.color,
                                                            background: activeRole.bgColor,
                                                            borderColor: activeRole.color + '40'
                                                        }}
                                                    >
                                                        {activeRole.label}
                                                    </span>
                                                    <span className="rp-detail-count">
                                                        Ve {(draftRolePermissions[activeRole.value] || []).length} de {ALL_MODULE_PATHS.length} módulos
                                                    </span>
                                                </div>

                                                <div className="rp-groups">
                                                    {Object.entries(permissionModulesByGroup).map(([group, modules]) => (
                                                        <div key={group} className="rp-group">
                                                            <h3 className="rp-group-title">{group}</h3>
                                                            <div className="rp-module-list">
                                                                {modules.map(mod => {
                                                                    const hasAccessDraft = (draftRolePermissions[activeRole.value] || []).includes(mod.path);
                                                                    const hasAccessSaved = (rolePermissions[activeRole.value] || []).includes(mod.path);
                                                                    const isDirtyRow = hasAccessDraft !== hasAccessSaved;
                                                                    const isNew = !mod.accion && isNewModule(mod.path);
                                                                    return (
                                                                        <button
                                                                            key={mod.path}
                                                                            type="button"
                                                                            aria-pressed={hasAccessDraft}
                                                                            className={`rp-module ${hasAccessDraft ? 'is-checked' : ''} ${isDirtyRow ? 'is-dirty' : ''}`}
                                                                            onClick={() => handleToggleRolePermission(activeRole.value, mod.path)}
                                                                        >
                                                                            <span className="rp-check">
                                                                                {hasAccessDraft ? <Check size={12} /> : null}
                                                                            </span>
                                                                            <span className="rp-module-info">
                                                                                <span className="rp-module-name">
                                                                                    {mod.label}
                                                                                    {isNew && (
                                                                                        <span className="rp-new-badge">
                                                                                            <Sparkles size={9} />
                                                                                            Nuevo
                                                                                        </span>
                                                                                    )}
                                                                                </span>
                                                                                <span className="rp-module-path">{mod.path}</span>
                                                                            </span>
                                                                            {isDirtyRow && (
                                                                                <span className="rp-module-pending" title="Cambio sin guardar" />
                                                                            )}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {/* ============================================
                        SECTION: CORREOS DE NOTIFICACIÓN
                       ============================================ */}
                    {activeSection === 'correos' && (
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="settings-section-header">
                                <h2>Correos de Notificación</h2>
                                <p>Activa el envío de correo para cada tipo de notificación. El correo llega a los mismos destinatarios de la notificación in-app.</p>
                            </div>

                            <div className="settings-card">
                                <div className="settings-card-header">
                                    <div className="settings-card-header-left">
                                        <Mail size={16} />
                                        <h3>Tipos de Notificación</h3>
                                    </div>
                                </div>

                                <div className="settings-card-body" style={{ padding: '0' }}>
                                    {emailConfigsLoading ? (
                                        <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                                            <RefreshCw size={20} className="spin" />
                                        </div>
                                    ) : (
                                        <div className="email-config-list">
                                            {emailConfigs.map(config => {
                                                const enabled = isEmailEnabledForType(config.tipo);
                                                return (
                                                    <div key={config.tipo} className={`email-config-item ${!enabled ? 'inactive' : ''}`}>
                                                        <div className="email-config-info">
                                                            <div className="email-config-top">
                                                                <span className="email-config-type">{config.label || config.tipo}</span>
                                                            </div>
                                                            <p className="email-config-desc">{config.desc}</p>
                                                            {config.destinos_email && (
                                                                <p className="email-config-desc" style={{ marginTop: '0.35rem', color: '#64748b' }}>
                                                                    Destinatarios: {config.destinos_email}
                                                                </p>
                                                            )}
                                                            <div className="email-recipient-controls">
                                                                <label className="email-recipient-check">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!config.include_notification_recipients}
                                                                        onChange={() => handleToggleNotificationRecipients(config.tipo)}
                                                                    />
                                                                    <span>Mismos destinatarios de la notificación</span>
                                                                </label>

                                                                <div className="email-role-grid">
                                                                    {ROLES.filter(role => role.value !== 'user').map(role => {
                                                                        const selected = (config.recipient_roles || []).includes(role.value);
                                                                        const roleInfo = getRoleInfo(role.value);
                                                                        return (
                                                                            <button
                                                                                key={`${config.tipo}-${role.value}`}
                                                                                type="button"
                                                                                className={`email-role-chip ${selected ? 'selected' : ''}`}
                                                                                style={{
                                                                                    borderColor: selected ? roleInfo.color : '#dbe2ea',
                                                                                    background: selected ? roleInfo.bgColor : '#fff',
                                                                                    color: selected ? roleInfo.color : '#64748b'
                                                                                }}
                                                                                onClick={() => handleToggleEmailRole(config.tipo, role.value)}
                                                                            >
                                                                                {role.label}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <button
                                                            className="icon-btn"
                                                            onClick={() => handleToggleEmailType(config.tipo)}
                                                            title={enabled ? 'Desactivar correo' : 'Activar correo'}
                                                        >
                                                            {enabled
                                                                ? <ToggleRight size={22} color="#22c55e" />
                                                                : <ToggleLeft size={22} color="#cbd5e1" />
                                                            }
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* ============================================
                        SECTION: DEBUG
                       ============================================ */}
                    {activeSection === 'debug' && (
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="settings-section-header">
                                <h2>Debug</h2>
                                <p>Herramientas de diagnóstico del sistema</p>
                            </div>

                            <div className="debug-section">
                                {/* Notification diagnostics */}
                                <div className="settings-card">
                                    <div className="settings-card-header">
                                        <div className="settings-card-header-left">
                                            <Bell size={16} />
                                            <h3>Notificaciones</h3>
                                        </div>
                                    </div>
                                    <div className="settings-card-body" style={{ padding: '1.15rem' }}>
                                        {/* Status cards */}
                                        <div className="debug-status-grid">
                                            <div className={`debug-status-card ${notifPermission === 'granted' ? 'status-ok' : notifPermission === 'denied' ? 'status-error' : 'status-warning'}`}>
                                                <div className="debug-status-icon-row">
                                                    {notifPermission === 'granted'
                                                        ? <CheckCircle2 size={18} color="#22c55e" />
                                                        : notifPermission === 'denied'
                                                            ? <XCircle size={18} color="#ef4444" />
                                                            : <Info size={18} color="#f59e0b" />
                                                    }
                                                    <span>Permiso del Navegador</span>
                                                </div>
                                                <p>
                                                    {notifPermission === 'granted' && 'Concedido'}
                                                    {notifPermission === 'denied' && 'Bloqueado - habilitar en config del navegador'}
                                                    {notifPermission === 'default' && 'Sin decidir - necesitas dar permiso'}
                                                    {notifPermission === 'unsupported' && 'No soportado'}
                                                </p>
                                                {notifPermission !== 'granted' && notifPermission !== 'unsupported' && (
                                                    <button className="debug-permission-btn" onClick={handleRequestPermission}>
                                                        <Bell size={13} /> Pedir permiso
                                                    </button>
                                                )}
                                            </div>

                                            <div className={`debug-status-card ${socketConnected ? 'status-ok' : 'status-error'}`}>
                                                <div className="debug-status-icon-row">
                                                    {socketConnected ? <Wifi size={18} color="#22c55e" /> : <WifiOff size={18} color="#ef4444" />}
                                                    <span>Socket.IO</span>
                                                </div>
                                                <p>
                                                    {socketConnected
                                                        ? 'Conectado - push en tiempo real'
                                                        : 'Desconectado - solo polling'}
                                                </p>
                                            </div>

                                            <div className="debug-status-card status-info">
                                                <div className="debug-status-icon-row">
                                                    <Eye size={18} color="#8b5cf6" />
                                                    <span>Visibilidad</span>
                                                </div>
                                                <p>Las notificaciones solo aparecen cuando el tab no está enfocado</p>
                                            </div>
                                        </div>

                                        {/* Test buttons */}
                                        <div className="debug-tests-card" style={{ marginTop: '0.85rem', border: 'none', padding: '0.85rem 0 0' }}>
                                            <h3><BellRing size={16} /> Pruebas</h3>
                                            <div className="debug-test-buttons">
                                                <button className="debug-test-btn test-local" onClick={handleLocalNotification}>
                                                    <Bell size={14} /> 1. Local
                                                </button>
                                                <button className="debug-test-btn test-bg" onClick={handleBackgroundNotification}>
                                                    <EyeOff size={14} /> 2. Background (5s)
                                                </button>
                                                <button className="debug-test-btn test-server" onClick={handleServerNotification} disabled={serverTestLoading}>
                                                    {serverTestLoading ? <RefreshCw size={14} className="spin" /> : <Send size={14} />}
                                                    3. Servidor
                                                </button>
                                            </div>
                                            <p className="debug-tests-hint">
                                                <strong>1.</strong> Verifica local. <strong>2.</strong> Verifica background (cambia de tab). <strong>3.</strong> Flujo completo: BD + Socket + navegador.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Log */}
                                <div className="debug-log">
                                    <div className="debug-log-header">
                                        <span>LOG DE DIAGNÓSTICO</span>
                                        <button className="debug-log-clear" onClick={() => setNotifTestLog([])}>
                                            Limpiar
                                        </button>
                                    </div>
                                    {notifTestLog.length === 0 ? (
                                        <p className="debug-log-empty">Haz click en un botón de prueba para comenzar...</p>
                                    ) : (
                                        notifTestLog.map(entry => (
                                            <div key={entry.id} className={`debug-log-entry log-${entry.type}`}>
                                                <span className="log-time">[{entry.time}]</span>{' '}
                                                {entry.type === 'success' ? '✓' : entry.type === 'error' ? '✗' : entry.type === 'warning' ? '⚠' : 'ℹ'}{' '}
                                                {entry.message}
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* Anuncios debug */}
                                <div className="settings-card" style={{ marginTop: '1rem' }}>
                                    <div className="settings-card-header">
                                        <div className="settings-card-header-left">
                                            <Mail size={16} />
                                            <h3>Anuncios (Email GH)</h3>
                                        </div>
                                    </div>
                                    <div className="settings-card-body" style={{ padding: '1.15rem' }}>
                                        <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                                            Sincroniza los correos de Gestión Humana que se muestran como anuncios en el feed del Home.
                                        </p>
                                        <div className="debug-test-buttons">
                                            <button
                                                className="debug-test-btn test-server"
                                                onClick={handleRefreshAnuncios}
                                                disabled={anunciosRefreshing}
                                            >
                                                {anunciosRefreshing
                                                    ? <RefreshCw size={14} className="spin" />
                                                    : <RefreshCw size={14} />
                                                }
                                                {anunciosRefreshing ? 'Sincronizando...' : 'Sincronizar Anuncios'}
                                            </button>
                                        </div>
                                        {anunciosResult && (
                                            <div className={`debug-log-entry log-${anunciosResult.type}`} style={{ marginTop: '0.75rem' }}>
                                                {anunciosResult.type === 'success' ? '✓' : '✗'}{' '}
                                                {anunciosResult.message}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </main>
            </div>

            {/* Floating Save/Cancel Bar — sticky bottom whenever there are pending changes */}
            <AnimatePresence>
                {isDirty && (
                    <motion.div
                        className="settings-savebar"
                        initial={{ y: 80, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 80, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                    >
                        <div className="settings-savebar-inner">
                            <div className="settings-savebar-info">
                                <span className="settings-savebar-dot" />
                                <span className="settings-savebar-text">
                                    {totalPendingChanges} {totalPendingChanges === 1 ? 'cambio pendiente' : 'cambios pendientes'}
                                </span>
                                {modulesChangesCount > 0 && (
                                    <span className="settings-savebar-pill">
                                        Módulos: {modulesChangesCount}
                                    </span>
                                )}
                                {permissionsChangesCount > 0 && (
                                    <span className="settings-savebar-pill">
                                        Permisos: {permissionsChangesCount}
                                    </span>
                                )}
                            </div>
                            <div className="settings-savebar-actions">
                                <button
                                    className="settings-savebar-cancel"
                                    onClick={handleCancelChanges}
                                    disabled={savingChanges}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="settings-savebar-save"
                                    onClick={handleSaveChanges}
                                    disabled={savingChanges}
                                >
                                    {savingChanges ? (
                                        <>
                                            <RefreshCw size={14} className="spin" />
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <Check size={14} />
                                            Guardar cambios
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default SettingsView;
